import querystring from 'querystring';
import React, {useEffect, useState} from 'react';
import config from '../config/config';
import { connect } from 'react-redux';
import {isBizzaboInternalWebsiteWhiteLabeled} from '@bizzabo/whitelabel-utils';
import { getInternalWebsiteBaseUrl } from 'web-common/scripts/utils/whitelabelUtils';
import { LoginWidgetScreen } from '@bizzabo/attendee-auth-api';
import AttendeeLogin from './reactViews/AttendeeLogin';
import {
    accountIdSelector,
    eventGroupIdSelector,
    eventIdSelector,
    eventUniqueNameSelector,
    onlyEventDomainSelector,
    websiteTypeSelector,
    whitelabelPathSelector,
    whitelabelUrlSelector,
    whitelabelWidgetEnabledSelector
} from '../selectors/EventSelectors';
import {
    inviteListFontFamilySelector,
    inviteListHeadlineSelector,
    isInviteListLoginSelector,
    loginLoadingStateSelector,
    ssoAccountIdSelector
} from '../selectors/LoginSelectors';

// Mirrors the server-side default in server/modules/GateHeadline.js; only
// reached if the page was rendered without the resolved value.
export const DEFAULT_GATE_HEADLINE = 'This event is by invitation only';

// A floor under the spinner. AttendeeLogin clears the loading flag from the
// iframe's onNotifyLoad, but the paths where that event never fires - blocked
// third-party cookies, a load error, an origin that never answers - would
// otherwise leave it spinning for good. Showing the widget after this is
// strictly better: either it arrived late, or the visitor can see it failed.
export const WIDGET_LOAD_TIMEOUT_MS = 20000;

export const InviteListGate = ({headline, fontFamily, isWidgetLoading, pageReferer}) => {
    const [hasTimedOut, setHasTimedOut] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => setHasTimedOut(true), WIDGET_LOAD_TIMEOUT_MS);
        return () => clearTimeout(timer);
    }, []);

    const showSpinner = isWidgetLoading && !hasTimedOut;

    return (
        <div className="private-event--invite-list">
            <p className="private-event__title custom-font">{headline}</p>
            {showSpinner && (
                <div className="gate-widget-spinner" role="status" aria-label="Loading sign-in">
                    <span className="gate-widget-spinner__ring" />
                </div>
            )}
            {/* Kept mounted while the spinner shows - the iframe has to be in the
                document to load at all - and hidden rather than unmounted so it
                does not restart. */}
            <div className={showSpinner ? 'gate-widget gate-widget--loading' : 'gate-widget'}>
                <AttendeeLogin
                    screen={LoginWidgetScreen.login}
                    redirectUrl={pageReferer}
                    closable={false}
                    isShownByDefault
                    inline
                    inviteListGated
                    fontFamily={fontFamily}
                />
            </div>
        </div>
    );
};

export const PrivateEventLoginScreen = ({
    onlyEventDomain,
    websiteType,
    whitelabelPath,
    whitelabelUrl,
    whitelabelWidgetEnabled,
    eventUniqueName,
    eventId,
    accountId,
    eventGroupId,
    ssoAccountId,
    isInviteListLogin,
    inviteListHeadline,
    inviteListFontFamily,
    loginV2LoadingState,
    pageReferer,
    event = {}
}) => {
    const {'header-logo-url': imgUrl } = event;

    if (isInviteListLogin) {
        // The event's branding is carried by the blurred website backdrop behind
        // this card (see privateEventPage.hbs), so the screen itself is just the
        // line above the login widget.
        return (
            <InviteListGate
                headline={inviteListHeadline || DEFAULT_GATE_HEADLINE}
                fontFamily={inviteListFontFamily}
                isWidgetLoading={!!loginV2LoadingState?.isLoading}
                pageReferer={pageReferer}
            />
        );
    }

    const internalWebsiteUrl = getInternalWebsiteBaseUrl(
        {
            websiteType,
            whitelabelWidgetEnabled,
            whitelabelUrl,
            whitelabelPath,
            onlyEventDomain,
            uniqueName: eventUniqueName,
        }
    );
    const whitelabelURL = isBizzaboInternalWebsiteWhiteLabeled({
        websiteType,
        whitelabelWidgetEnabled,
    })
        ? whitelabelUrl
        : null;
    const queryParams = querystring.stringify({
        pageReferer,
        eventId,
        groupId: eventGroupId,
        accountId,
        whitelabelURL,
        redirectTo: pageReferer,
        internalWebsiteUrl,
    });
    const urlToIDP = `${config.apiServer}/api/v2/sso/accounts/${accountId}/config/${ssoAccountId}/login-url?${queryParams}`;
    return (
        <div>
            {imgUrl && (
                <div className="private-event__logo">
                    <img
                        src={imgUrl}
                        className="event-logo"
                        alt="Event Logo"
                    />
                </div>
            )}

            <p className="private-event__title">This is a private event for invitees only</p>
            <a href={urlToIDP} className="cta-button cta-button-medium font-size-13 custom-color color-1-background color-1-hover-opacity">Enter</a>
        </div>
    );
};

export default connect(state => ({
    onlyEventDomain: onlyEventDomainSelector(state),
    websiteType: websiteTypeSelector(state),
    whitelabelPath: whitelabelPathSelector(state),
    ssoAccountId: ssoAccountIdSelector(state),
    whitelabelUrl: whitelabelUrlSelector(state),
    eventUniqueName: eventUniqueNameSelector(state),
    accountId: accountIdSelector(state),
    eventId: eventIdSelector(state),
    eventGroupId: eventGroupIdSelector(state),
    whitelabelWidgetEnabled: whitelabelWidgetEnabledSelector(state),
    isInviteListLogin: isInviteListLoginSelector(state),
    inviteListHeadline: inviteListHeadlineSelector(state),
    inviteListFontFamily: inviteListFontFamilySelector(state),
    loginV2LoadingState: loginLoadingStateSelector(state),
}), null)(PrivateEventLoginScreen);
